import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import clsx from "clsx";
import { useIsFirstRender, useDebounce } from "usehooks-ts";

type Value = string;

export type TextareaProps = Omit<React.HTMLAttributes<HTMLTextAreaElement>, "onChange"> & {
  placeholder: string;
  onChange: (value: Value) => void; // debounced
  defaultValue?: Value;
  updateValueIfDefaultChanges?: boolean;
  debounceValue?: number;
  className?: string;
  label?: string;
  labelClassName?: string;
  error?: string;
  "data-testid"?: string;
  fitHeightToContent?: boolean;
  disabled?: boolean;
  maxLength?: number;
};

export function Textarea({
  placeholder,
  onChange,
  defaultValue,
  debounceValue,
  className: classNameExt,
  label,
  labelClassName,
  error,
  "data-testid": dataTestId,
  fitHeightToContent = false,
  updateValueIfDefaultChanges = true,
  disabled,
  maxLength,
  ...textareaProps
}: TextareaProps) {
  const isFirstRender = useIsFirstRender();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState<Value>(defaultValue ?? "");
  const debouncedValue = useDebounce<Value>(value, debounceValue ?? 500);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
  };

  const originalHeight = useMemo(() => {
    if (textAreaRef.current) {
      return textAreaRef.current.scrollHeight;
    }
    return 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textAreaRef.current]);

  /**
   * Update value to defaultValue in case defaultValue is fetched async and this component
   * is rendered already
   */
  useEffect(() => {
    updateValueIfDefaultChanges && defaultValue && setValue(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  useEffect(() => {
    if (isFirstRender) return;
    onChange(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  // Useful when printing and the whole content needs to be visible.
  useEffect(() => {
    if (!fitHeightToContent || !textAreaRef.current) return;

    // Reset height to auto to calculate the new scrollHeight
    if (debouncedValue === "" && originalHeight > 0) {
      // Reset height to original height when the value is empty
      textAreaRef.current.style.height = originalHeight + "px";
      return;
    }

    textAreaRef.current.style.height = textAreaRef.current.scrollHeight + "px";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  const className = clsx(
    "block",
    "w-full",
    "border",
    "rounded-md",
    "border-solid",
    !error && "border-neutral-300",
    !error && "hover:border-platform-600",
    "hover:outline-hidden",
    "focus:border",
    !error && "focus:border-platform-600",
    "focus:outline-hidden",
    "placeholder-neutral-400",
    "placeholder-text-sm",
    "py-2",
    "px-3",
    error && "border-red-700",
    classNameExt
  );

  return (
    <label className={clsx("relative!", "w-full", error && "text-red-700")}>
      <span className={clsx("block", "mb-1", labelClassName)}>{label}</span>
      <textarea
        {...textareaProps}
        ref={textAreaRef}
        value={value}
        onChange={handleChange}
        className={className}
        placeholder={placeholder}
        rows={3}
        data-testid={`${dataTestId ?? "textarea-input"}`}
        disabled={disabled}
        maxLength={maxLength}
      />
      {error && (
        <span
          data-testid={`${dataTestId ?? "textare-input"}-error-message`}
          className="absolute right-0 text-xs text-red-700 -bottom-1"
        >
          {error}
        </span>
      )}
    </label>
  );
}
